import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';

const PricingPage = () => {
  const [isAnnual, setIsAnnual] = useState(false);
  const [iconIndex, setIconIndex] = useState(0);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [stickyNoteIndex, setStickyNoteIndex] = useState(0);
  
  const { user, initializePayment, verifyPayment, loading: authLoading } = useAuth();

  useEffect(() => {
    const icons = ['🌍', '🧠', '🎯', '🔍', '📊', '🚀'];
    const interval = setInterval(() => {
      setIconIndex((prev) => (prev + 1) % icons.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Rotate sticky notes
  useEffect(() => {
    const stickyInterval = setInterval(() => {
      setStickyNoteIndex((prev) => (prev + 1) % 15);
    }, 5000);
    return () => clearInterval(stickyInterval);
  }, []);

  useEffect(() => {
    const handlePaymentCallback = async () => {
      const paymentStatus = searchParams.get('payment');
      const reference = searchParams.get('reference');
      
      if (paymentStatus === 'success' && reference && user) {
        setIsProcessingPayment(true);
        
        try {
          const result = await verifyPayment(reference);
          
          if (result.success) {
            setPaymentSuccess('Payment successful! Welcome to Marketing Intelligence Pro!');
            navigate('/pricing', { replace: true });
            setTimeout(() => {
              navigate('/dashboard');
            }, 3000);
          } else {
            setPaymentError(result.error || 'Payment verification failed');
          }
        } catch (error) {
          setPaymentError('Error verifying payment. Please contact support.');
        } finally {
          setIsProcessingPayment(false);
        }
      }
    };

    handlePaymentCallback();
  }, [searchParams, user, verifyPayment, navigate]);

  const handleTrialClick = () => {
    if (!user) {
      navigate('/signup');
      return;
    }
    navigate('/dashboard');
  };

  const handlePaymentClick = async () => {
    if (!user) {
      navigate('/signup');
      return;
    }

    setIsProcessingPayment(true);
    setPaymentError('');
    
    try {
      const planType = isAnnual ? 'annual' : 'monthly';
      const result = await initializePayment(planType);
      
      if (result.success) {
        window.location.href = result.authorization_url;
      } else {
        setPaymentError(result.error || 'Failed to initialize payment');
        setIsProcessingPayment(false);
      }
    } catch (error) {
      setPaymentError('Payment initialization failed. Please try again.');
      setIsProcessingPayment(false);
    }
  };

  const stickyNotes = [
    {
      text: "Your competitor just closed another campaign. You're still guessing why yours died. 💀",
      color: '#ffd93d',
      rotate: -3
    },
    {
      text: "That $5k you 'tested' last month? Yeah, we would've told you that wouldn't work. 🤷",
      color: '#ff6b9d',
      rotate: 2
    },
    {
      text: "Free trial = Find out what's broken. Upgrade = Fix it and never break it again. Math is simple. 🧮",
      color: '#6bcf7f',
      rotate: -2
    },
    {
      text: "\"I'll just try harder next time\" - Famous last words of marketers with no data 📉",
      color: '#c77dff',
      rotate: 3
    },
    {
      text: "While you're reading this, someone in your industry just discovered their winning platform. Spoiler: It's not where you're spending. 👀",
      color: '#4cc9f0',
      rotate: -4
    },
    {
      text: "7 days to either validate your strategy or realize you've been throwing money into a void. Your call. ⏰",
      color: '#ff9770',
      rotate: 1
    },
    {
      text: "\"But we've always done it this way\" - said every marketer before they got replaced by someone who uses data 🤖",
      color: '#ffadad',
      rotate: -2
    },
    {
      text: "Plot twist: Your 'winning' creative isn't winning. Your targeting is just lucky. We'll show you the difference. 🎯",
      color: '#caffbf',
      rotate: 4
    },
    {
      text: "That agency charging you $10k/month? They're guessing too. At least we're honest about it (and cheaper). 💸",
      color: '#9bf6ff',
      rotate: -3
    },
    {
      text: "\"Let's boost the post\" - Translation: \"I have no idea what I'm doing but this button looks important\" 🔘",
      color: '#ffc6ff',
      rotate: 2
    },
    {
      text: "You: *increases budget* Algorithm: *laughs in CPM* Us: *shows you why* 📊",
      color: '#fdffb6',
      rotate: -1
    },
    {
      text: "Everyone's an expert until you ask them WHY their campaign failed. Then suddenly... crickets. 🦗",
      color: '#ffcfd2',
      rotate: 3
    },
    {
      text: "\"Trust your gut\" is terrible advice when your gut doesn't understand ad platforms. Just saying. 🎲",
      color: '#a0c4ff',
      rotate: -2
    },
    {
      text: "Your CMO wants results. You want to keep your job. We want to help. See? Everyone wins. 🏆",
      color: '#ffb3ba',
      rotate: 1
    },
    {
      text: "Remember that 'viral' campaign idea? Yeah, we would've caught that red flag in day 1. 🚩",
      color: '#bae1ff',
      rotate: -3
    }
  ];

  const icons = ['🌍', '🧠', '🎯', '🔍', '📊', '🚀'];

  if (authLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0a0a0a',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🔍</div>
          <div>Loading your account...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      backgroundColor: '#0a0a0a',
      color: 'white',
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      {/* Payment Status Messages */}
      {paymentSuccess && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.9), rgba(34, 197, 94, 0.8))',
          color: 'white',
          padding: '15px 30px',
          borderRadius: '12px',
          zIndex: 1000,
          fontWeight: '600',
          boxShadow: '0 10px 30px rgba(16, 185, 129, 0.3)'
        }}>
          {paymentSuccess}
        </div>
      )}

      {paymentError && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.9), rgba(248, 113, 113, 0.8))',
          color: 'white',
          padding: '15px 30px',
          borderRadius: '12px',
          zIndex: 1000,
          fontWeight: '600',
          boxShadow: '0 10px 30px rgba(239, 68, 68, 0.3)'
        }}>
          {paymentError}
        </div>
      )}

      {isProcessingPayment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 999,
          backdropFilter: 'blur(5px)'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            padding: '30px',
            borderRadius: '20px',
            textAlign: 'center',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '20px', animation: 'spin 1s linear infinite' }}>🔄</div>
            <div style={{ fontSize: '1.2rem', fontWeight: '600' }}>Processing your payment...</div>
          </div>
        </div>
      )}

      {/* Subtle gradient orbs in background */}
      <div style={{
        position: 'fixed',
        top: '10%',
        left: '5%',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(224, 122, 95, 0.08) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(60px)',
        pointerEvents: 'none',
        zIndex: 0,
        animation: 'float 20s ease-in-out infinite'
      }} />
      
      <div style={{
        position: 'fixed',
        bottom: '5%',
        right: '10%',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(255, 107, 107, 0.06) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(70px)',
        pointerEvents: 'none',
        zIndex: 0,
        animation: 'float 25s ease-in-out infinite reverse'
      }} />

      <div style={{
        position: 'fixed',
        top: '50%',
        right: '5%',
        width: '350px',
        height: '350px',
        background: 'radial-gradient(circle, rgba(224, 122, 95, 0.05) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(50px)',
        pointerEvents: 'none',
        zIndex: 0,
        animation: 'float 30s ease-in-out infinite'
      }} />

      {/* Main Container */}
      <div style={{ 
        position: 'relative',
        zIndex: 2,
        width: '100%',
        maxWidth: '1200px'
      }}>
        {/* Header Section - Condensed */}
        <div style={{ 
          textAlign: 'center',
          marginBottom: '30px',
          animation: 'fadeInDown 1s ease-out'
        }}>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '900',
            marginBottom: '15px',
            lineHeight: '1.1',
            color: 'white'
          }}>
            <span style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #e07a5f 50%, #ff6b6b 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              WhyDidItDie
            </span>
          </h1>
          
          <p style={{
            fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
            color: 'rgba(255, 255, 255, 0.85)',
            maxWidth: '600px',
            margin: '0 auto 20px',
            lineHeight: '1.5'
          }}>
            Stop wondering <span style={{ color: '#e07a5f', fontWeight: 'bold' }}>"what went wrong?"</span><br />
            Start knowing <span style={{ color: '#e07a5f', fontWeight: 'bold' }}>"what works."</span>
          </p>
        </div>
        
        {/* Toggle Switch */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: '40px',
          gap: '15px',
          animation: 'fadeIn 1.2s ease-out',
          flexWrap: 'wrap'
        }}>
          <span style={{
            color: !isAnnual ? 'white' : 'rgba(255, 255, 255, 0.5)',
            fontSize: '1rem',
            fontWeight: '600',
            transition: 'opacity 0.3s'
          }}>
            Monthly
          </span>
          <div
            onClick={() => setIsAnnual(!isAnnual)}
            style={{
              position: 'relative',
              width: '60px',
              height: '30px',
              background: 'linear-gradient(135deg, #e07a5f, #ff6b6b)',
              borderRadius: '30px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(224, 122, 95, 0.3)'
            }}
          >
            <div style={{
              position: 'absolute',
              width: '24px',
              height: '24px',
              background: 'white',
              borderRadius: '50%',
              top: '3px',
              left: isAnnual ? '33px' : '3px',
              transition: 'transform 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
            }} />
          </div>
          <span style={{
            color: isAnnual ? 'white' : 'rgba(255, 255, 255, 0.5)',
            fontSize: '1rem',
            fontWeight: '600',
            transition: 'opacity 0.3s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            Annual
            {isAnnual && (
              <span style={{
                background: 'linear-gradient(135deg, #10b981, #34d399)',
                color: 'white',
                padding: '3px 10px',
                borderRadius: '15px',
                fontSize: '0.75rem',
                fontWeight: '700',
                animation: 'bounce 3s infinite',
                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)'
              }}>
                1 month free
              </span>
            )}
          </span>
        </div>

        {/* Sticky Notes - Responsive positioning */}
        <div style={{
          position: 'relative',
          width: '100%',
          maxWidth: '900px',
          margin: '0 auto 30px',
          minHeight: window.innerWidth < 768 ? '120px' : '80px',
          overflow: 'visible'
        }}>
          {stickyNotes.map((note, idx) => {
            const getNotePosition = () => {
              if (window.innerWidth < 768) {
                return '50%';
              }
              
              const positions = [5, 15, 25, 35, 45, 55, 65, 75, 85];
              const posIndex = idx % positions.length;
              return `${positions[posIndex]}%`;
            };
            
            return (
              <div
                key={idx}
                style={{
                  position: 'absolute',
                  left: getNotePosition(),
                  top: window.innerWidth < 768 ? '0' : `${idx % 2 === 0 ? '-10px' : '10px'}`,
                  transform: window.innerWidth < 768 
                    ? `translateX(-50%) rotate(${note.rotate}deg)` 
                    : `translateX(-50%) rotate(${note.rotate}deg)`,
                  background: note.color,
                  padding: window.innerWidth < 768 ? '12px 15px' : '15px 20px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
                  fontFamily: "'Comic Sans MS', cursive, sans-serif",
                  fontSize: window.innerWidth < 768 ? '0.75rem' : '0.85rem',
                  fontWeight: '600',
                  color: '#2a2a2a',
                  maxWidth: window.innerWidth < 768 ? '280px' : '200px',
                  lineHeight: '1.3',
                  opacity: idx === stickyNoteIndex ? 1 : 0,
                  transition: 'opacity 0.5s ease-in-out',
                  zIndex: idx === stickyNoteIndex ? 10 : 1,
                  pointerEvents: 'none',
                  border: '1px solid rgba(0,0,0,0.1)',
                  textAlign: 'center'
                }}
              >
                {note.text}
              </div>
            );
          })}
        </div>

        {/* Pricing Cards Container - Smaller, More Compact */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(2, 1fr)',
          gap: window.innerWidth < 768 ? '20px' : '25px',
          padding: '10px',
          marginBottom: '30px',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          {/* Free Trial Card - Compact */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.03))',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            padding: window.innerWidth < 768 ? '30px 20px' : '35px 25px',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            position: 'relative',
            overflow: 'visible',
            animation: 'slideUp 1s ease-out 0.2s both',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 20px 40px rgba(224, 122, 95, 0.2)';
            e.currentTarget.style.borderColor = '#e07a5f';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
          }}
          >
            <div style={{
              position: 'absolute',
              top: '-12px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'linear-gradient(135deg, #e07a5f, #ff6b6b)',
              color: 'white',
              padding: '5px 15px',
              borderRadius: '15px',
              fontSize: '0.75rem',
              fontWeight: '700',
              boxShadow: '0 4px 15px rgba(224, 122, 95, 0.4)',
              whiteSpace: 'nowrap'
            }}>
              🔍 Risk-Free
            </div>
            
            <h2 style={{
              fontSize: window.innerWidth < 768 ? '1.4rem' : '1.6rem',
              fontWeight: '900',
              color: 'white',
              marginBottom: '8px',
              marginTop: '10px',
              textAlign: 'center'
            }}>
              14-Day Free Trial
            </h2>
            <p style={{
              color: 'rgba(255, 255, 255, 0.7)',
              textAlign: 'center',
              marginBottom: '20px',
              fontSize: '0.9rem',
              lineHeight: '1.4'
            }}>
              Find what's working (spoiler: not what you think)
            </p>
            
            <div style={{
              textAlign: 'center',
              marginBottom: '20px'
            }}>
              <div style={{
                fontSize: '2.5rem',
                fontWeight: '900',
                color: '#e07a5f',
                lineHeight: '1'
              }}>
                $0
              </div>
              <div style={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.85rem',
                marginTop: '3px'
              }}>
                Full access, 2 days
              </div>
            </div>

            <ul style={{ listStyle: 'none', marginBottom: '20px', padding: 0 }}>
              {[
                '📊 Campaign Detective',
                '🕵🏽 Waste Detective',
                '🎯 Platform Intelligence',
                '🧠 Market Intelligence',
                '🤖 AI Content Engine',
                '👑 Decision Tracker'
              ].map((feature, idx) => (
                <li key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '8px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.85rem',
                  padding: '6px 8px',
                  background: 'rgba(255, 255, 255, 0.02)',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(224, 122, 95, 0.08)';
                  e.currentTarget.style.transform = 'translateX(3px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}>
                  {feature}
                </li>
              ))}
            </ul>

            <button
              onClick={handleTrialClick}
              disabled={isProcessingPayment}
              style={{
                width: '100%',
                padding: '14px',
                border: '2px solid #e07a5f',
                background: 'transparent',
                color: '#e07a5f',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: '700',
                cursor: isProcessingPayment ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                opacity: isProcessingPayment ? 0.7 : 1
              }}
              onMouseEnter={e => {
                if (!isProcessingPayment) {
                  e.currentTarget.style.background = '#e07a5f';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={e => {
                if (!isProcessingPayment) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#e07a5f';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              {!user ? 'Start Free Trial' : 'Access Your Trial'}
            </button>
          </div>

          {/* Premium Card - Compact */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.03))',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            padding: window.innerWidth < 768 ? '30px 20px' : '35px 25px',
            border: '2px solid #e07a5f',
            position: 'relative',
            overflow: 'visible',
            animation: 'slideUp 1s ease-out 0.4s both',
            boxShadow: '0 15px 40px rgba(224, 122, 95, 0.2)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 20px 50px rgba(224, 122, 95, 0.3)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 15px 40px rgba(224, 122, 95, 0.2)';
          }}
          >
            <div style={{
              position: 'absolute',
              top: '-12px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'linear-gradient(135deg, #e07a5f, #ff6b6b)',
              color: 'white',
              padding: '5px 15px',
              borderRadius: '15px',
              fontSize: '0.75rem',
              fontWeight: '700',
              boxShadow: '0 4px 15px rgba(224, 122, 95, 0.4)',
              whiteSpace: 'nowrap'
            }}>
              🔬 Full Power
            </div>
            
            <h2 style={{
              fontSize: window.innerWidth < 768 ? '1.4rem' : '1.6rem',
              fontWeight: '900',
              color: 'white',
              marginBottom: '8px',
              marginTop: '10px',
              textAlign: 'center'
            }}>
              Marketing Intelligence
            </h2>
            <p style={{
              color: 'rgba(255, 255, 255, 0.7)',
              textAlign: 'center',
              marginBottom: '20px',
              fontSize: '0.9rem',
              lineHeight: '1.4'
            }}>
              For marketers ready to dominate globally
            </p>
            
            <div style={{
              textAlign: 'center',
              marginBottom: '20px'
            }}>
              <div style={{
                fontSize: '2.5rem',
                fontWeight: '900',
                color: '#e07a5f',
                lineHeight: '1'
              }}>
                ${isAnnual ? '165' : '15'}
              </div>
              <div style={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.85rem',
                marginTop: '3px'
              }}>
                {isAnnual ? 'per year (<$1/day)' : 'per month'}
              </div>
            </div>

            <ul style={{ listStyle: 'none', marginBottom: '20px', padding: 0 }}>
              {[
                '📊 Deep Campaign Analysis',
                '🕵🏽 Budget Forensics',
                '🎯 Platform Discovery',
                '🧠 Market Predictions',
                '🤖 Psych + Culture Content',
                '👑 Decision Accountability'
              ].map((feature, idx) => (
                <li key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '8px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.85rem',
                  padding: '6px 8px',
                  background: 'rgba(255, 255, 255, 0.02)',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(224, 122, 95, 0.08)';
                  e.currentTarget.style.transform = 'translateX(3px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}>
                  {feature}
                </li>
              ))}
            </ul>

            <button
              onClick={handlePaymentClick}
              disabled={isProcessingPayment}
              style={{
                width: '100%',
                padding: '14px',
                background: 'linear-gradient(135deg, #e07a5f, #ff6b6b)',
                border: 'none',
                color: 'white',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: '700',
                cursor: isProcessingPayment ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 20px rgba(224, 122, 95, 0.4)',
                opacity: isProcessingPayment ? 0.7 : 1
              }}
              onMouseEnter={e => {
                if (!isProcessingPayment) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 25px rgba(224, 122, 95, 0.5)';
                }
              }}
              onMouseLeave={e => {
                if (!isProcessingPayment) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(224, 122, 95, 0.4)';
                }
              }}
            >
              {isProcessingPayment ? 'Processing...' : `Upgrade - $${isAnnual ? '165/yr' : '15/mo'}`}
            </button>
            
            <div style={{
              textAlign: 'center',
              color: 'rgba(255, 255, 255, 0.5)',
              marginTop: '12px',
              fontSize: '0.8rem',
              fontStyle: 'italic'
            }}>
              or test with free trial first
            </div>
          </div>
        </div>

        {/* Bottom CTA Section - Compact */}
        <div style={{
          textAlign: 'center',
          padding: window.innerWidth < 768 ? '25px 20px' : '30px',
          background: 'linear-gradient(135deg, rgba(224, 122, 95, 0.1), rgba(255, 107, 107, 0.05))',
          borderRadius: '20px',
          border: '1px solid rgba(224, 122, 95, 0.2)',
          marginTop: '30px',
          maxWidth: '800px',
          margin: '30px auto 0'
        }}>
          <h2 style={{
            fontSize: window.innerWidth < 768 ? '1.5rem' : '1.8rem',
            fontWeight: '900',
            color: 'white',
            marginBottom: '12px'
          }}>
            Real Talk
          </h2>
          <p style={{
            fontSize: window.innerWidth < 768 ? '1rem' : '1.1rem',
            color: 'rgba(255, 255, 255, 0.8)',
            marginBottom: '20px',
            lineHeight: '1.5'
          }}>
            Why guess what works when we can show you exactly where to win?<br />
            <strong>Your competitors are already using smarter tools.</strong>
          </p>
          
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '15px',
            flexWrap: 'wrap',
            marginTop: '20px'
          }}>
            <div style={{
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              color: '#10b981',
              padding: '8px 16px',
              borderRadius: '8px',
              fontWeight: '700',
              fontSize: '0.8rem'
            }}>
              Simple billing
            </div>
            <div style={{
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              color: '#10b981',
              padding: '8px 16px',
              borderRadius: '8px',
              fontWeight: '700',
              fontSize: '0.8rem'
            }}>
              Cancel anytime
            </div>
            <div style={{
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              color: '#10b981',
              padding: '8px 16px',
              borderRadius: '8px',
              fontWeight: '700',
              fontSize: '0.8rem'
            }}>
              ROI guaranteed*
            </div>
          </div>
          
          <p style={{
            color: '#6b7280',
            fontSize: '0.7rem',
            marginTop: '12px'
          }}>
            *If we don't find waste or opportunities in your first month, we'll refund you. Seriously.
          </p>
        </div>

        {/* Current User Info Panel */}
        {user && (
          <div style={{
            textAlign: 'center',
            padding: '25px',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))',
            borderRadius: '15px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            marginTop: '25px',
            maxWidth: '600px',
            margin: '25px auto 0'
          }}>
            <h3 style={{
              fontSize: '1.3rem',
              fontWeight: '700',
              color: 'white',
              marginBottom: '12px'
            }}>
              Welcome back, Detective! 🔍
            </h3>
            
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                padding: '10px 25px',
                background: 'linear-gradient(135deg, #e07a5f, #ff6b6b)',
                border: 'none',
                color: 'white',
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 5px 20px rgba(224, 122, 95, 0.4)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Go to Dashboard
            </button>
          </div>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -30px) scale(1.05);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.95);
          }
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes bounce {
          0%, 100% {
            transform: translateY(-25%);
            animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
          }
          50% {
            transform: translateY(0);
            animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
          }
        }

        ::-webkit-scrollbar {
          width: 8px;
        }
        
        ::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
        }
        
        ::-webkit-scrollbar-thumb {
          background: rgba(224, 122, 95, 0.5);
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(224, 122, 95, 0.7);
        }
      `}</style>
    </div>
  );
};

export default PricingPage;
